/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import { AnnotationHandler } from "../annotations/AnnotationHandler";
import { DoublyLinkedList } from "./linked-list/DoublyLinkedList";
import { useEffect, useState } from "react";
import { Annotation, IAnnotation } from "../../types/Annotation";
import { MarkdownProps, MarkdownRenderer } from "@sandbox/markdown";
import { DoublyLinkedNode } from "./linked-list/DoublyLinkedNode";
import { WalkthroughFooter } from "./WalkthroughFooter";
import styles from "./WalkthroughHandler.module.css";
import { WalkthroughHeader } from "./WalkthroughHeader";

export type MarkdownOptions = Omit<MarkdownProps, "children">;

export interface WalkthroughHandlerProps {
  annotationHandler: AnnotationHandler;
  className?: string;
  activeStep?: DoublyLinkedNode<Annotation>;
  annotationSteps: DoublyLinkedList<Annotation>;
  markdownOptions?: MarkdownOptions;
  onStepChanged?: (step: DoublyLinkedNode<Annotation>) => void;
  onRecentered?: () => void;
  onFocusModeChanged?: (enabled: boolean) => void;
}

export const WalkthroughHandler = ({ annotationHandler, activeStep, annotationSteps, markdownOptions, className, onStepChanged, onRecentered, onFocusModeChanged }: WalkthroughHandlerProps) => {
  const [codeHighlight, setCodeHighlight] = useState(true);

  const onAnnotationClick = (annotation: IAnnotation) => {
    const findStep = (step: DoublyLinkedNode<Annotation>) => Boolean(
      step.value.startLineNumber === annotation.lineNumber &&
      step.value.startLineNumber === annotation.endLineNumber &&
      step.value.filePath &&
      annotation.uri.path === step.value.filePath);

    const step = annotationSteps.find(findStep);

    if (step && onStepChanged)
      onStepChanged(step);
  }

  useEffect(() => {
    const disposable = annotationHandler.onAnnotationClick(onAnnotationClick);
    return disposable.dispose;
  }, [annotationHandler])

  const onChanged = (step: DoublyLinkedNode<Annotation>) => {
    if (onStepChanged)
      onStepChanged(step);

    setCodeHighlight(true);
    onFocusModeChanged && onFocusModeChanged(true);
  };

  const onCodeHighlight = (highlighted: boolean) => {
    setCodeHighlight(highlighted);
    onFocusModeChanged && onFocusModeChanged(highlighted);
  };

  const onEmphasisClick = () => {
    setCodeHighlight(true);
    onRecentered && onRecentered();
  }

  return <div className={[styles.container, className ?? ""].join(" ")}>
    <WalkthroughHeader
      steps={annotationSteps}
      step={activeStep}
      onStepChange={onChanged}
      codeHighlight={codeHighlight}
      onCodeHighlight={onCodeHighlight}
    />
    <div className={styles.step}>
      <MarkdownRenderer {...markdownOptions}>
        {activeStep?.value?.markdown ?? ""}
      </MarkdownRenderer >
    </div>
    <WalkthroughFooter step={activeStep} onStepChange={onChanged} onEmphasisClick={onEmphasisClick} />
  </div>
}
